/**
 * MayaBubble — the popover that delivers Maya's tip for the open beacon.
 *
 * Design intent:
 *   • Frosted glass bubble with brand rose-gold border + soft drop shadow.
 *   • Maya's avatar anchored in the top-left of the bubble — instant
 *     recognition, consistent voice with the rest of the product.
 *   • Smart positioning: above / below / left / right based on the
 *     target's screen position. On mobile, docks to the bottom as a
 *     full-width sheet (familiar gesture, no clipping).
 *   • Subtle curved tail pointing at the target, with a gradient glow
 *     so it reads as "this card belongs to that element".
 *   • Three CTAs: primary "Got it ✓" (advances walk OR closes),
 *     secondary "Tell me more" (links to long-form help — Wave 2),
 *     ghost "Skip tour" (ends an auto-walk).
 *   • Premium entrance animation: translate-Y + opacity with a
 *     cubic-bezier(0.16, 1, 0.3, 1) ease — the same curve Linear and
 *     Stripe use for their bottom-sheet motion.
 *
 * The celebration moment ("__celebration__") renders the bubble
 * centred on the screen with a sparkle burst — no tail, no target.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles, ArrowRight, Play } from "lucide-react";

import useTour from "./useTour";
import MayaAvatar from "../../pages/shop/custom-studio/v4/components/MayaAvatar";

const BUBBLE_WIDTH = 320;
const BUBBLE_HEIGHT_GUESS = 200;
const GAP = 18;

function pickPlacement(rect, viewport) {
    // Try below first (most natural for a tip), then above, then right,
    // then left. Falls back to "centered" on tiny viewports.
    // iter357 — 768px breakpoint covers iPad portrait + all phones. On
    // these widths the floating-bubble math gets crowded, so we dock as
    // a bottom sheet instead — more thumb-friendly and never clips.
    if (!rect) return { mode: "center" };
    const spaceBelow = viewport.height - (rect.top + rect.height);
    const spaceAbove = rect.top;
    const spaceRight = viewport.width - (rect.left + rect.width);
    const spaceLeft = rect.left;
    if (viewport.width < 768) return { mode: "sheet" };
    if (spaceBelow >= BUBBLE_HEIGHT_GUESS + GAP) return { mode: "below" };
    if (spaceAbove >= BUBBLE_HEIGHT_GUESS + GAP) return { mode: "above" };
    if (spaceRight >= BUBBLE_WIDTH + GAP) return { mode: "right" };
    if (spaceLeft  >= BUBBLE_WIDTH + GAP) return { mode: "left" };
    return { mode: "center" };
}

function computeBubblePos(rect, mode, viewport) {
    if (!rect || mode === "center" || mode === "sheet") return null;
    let top, left;
    if (mode === "below") {
        top = rect.top + rect.height + GAP;
        left = rect.left + rect.width / 2 - BUBBLE_WIDTH / 2;
    } else if (mode === "above") {
        top = rect.top - BUBBLE_HEIGHT_GUESS - GAP;
        left = rect.left + rect.width / 2 - BUBBLE_WIDTH / 2;
    } else if (mode === "right") {
        top = rect.top + rect.height / 2 - BUBBLE_HEIGHT_GUESS / 2;
        left = rect.left + rect.width + GAP;
    } else { // left
        top = rect.top + rect.height / 2 - BUBBLE_HEIGHT_GUESS / 2;
        left = rect.left - BUBBLE_WIDTH - GAP;
    }
    // Clamp to viewport with a small margin.
    const margin = 12;
    left = Math.max(margin, Math.min(left, viewport.width - BUBBLE_WIDTH - margin));
    top  = Math.max(margin, Math.min(top,  viewport.height - 80));
    return { top, left };
}

// iter358.2 — Render body text and inline a tiny replica of the real
// Beacon glyph wherever the copy contains the `{badge}` token. Used
// most prominently in the celebration card so the customer SEES the
// actual badge shape/colour we want them to look for (instead of a
// bare red-looking "?" character that previous copy left dangling).
function InlineBeaconChip() {
    return (
        <span
            className="jns-tour-inline-badge"
            aria-hidden="true"
            data-testid="tour-inline-badge"
        >
            ?
        </span>
    );
}

function renderBodyWithBadge(text) {
    if (!text || typeof text !== "string" || !text.includes("{badge}")) return text;
    const parts = text.split("{badge}");
    const out = [];
    parts.forEach((segment, idx) => {
        if (segment) out.push(<span key={`s-${idx}`}>{segment}</span>);
        if (idx < parts.length - 1) out.push(<InlineBeaconChip key={`b-${idx}`} />);
    });
    return out;
}

export default function MayaBubble() {
    const {
        openBeaconId, closeBeacon, next, skip, getCopy,
        isAutoWalk, walkIndex, walkSequence,
        language, setLanguage, availableLanguages,
    } = useTour();
    const [rect, setRect] = useState(null);
    const [viewport, setViewport] = useState({
        width: typeof window !== "undefined" ? window.innerWidth : 1024,
        height: typeof window !== "undefined" ? window.innerHeight : 800,
    });
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!openBeaconId) {
            setVisible(false);
            return undefined;
        }
        if (openBeaconId === "__celebration__") {
            setRect(null);
            setVisible(true);
            // Auto-dismiss after a short celebratory beat.
            const t = setTimeout(() => closeBeacon(), 2400);
            return () => clearTimeout(t);
        }
        const t = document.querySelector(`[data-tour-target="${openBeaconId}"]`);
        const target = t ? (t.parentElement || t) : null;
        const measure = () => {
            if (target) {
                const r = target.getBoundingClientRect();
                setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
            }
            setViewport({ width: window.innerWidth, height: window.innerHeight });
        };
        const af = requestAnimationFrame(() => requestAnimationFrame(() => {
            measure(); setVisible(true);
        }));
        window.addEventListener("scroll", measure, true);
        window.addEventListener("resize", measure);
        return () => {
            cancelAnimationFrame(af);
            window.removeEventListener("scroll", measure, true);
            window.removeEventListener("resize", measure);
        };
    }, [openBeaconId, closeBeacon]);

    if (!openBeaconId || typeof document === "undefined") return null;

    const copy = getCopy(openBeaconId);
    if (!copy) return null;

    const isCelebration = openBeaconId === "__celebration__";
    const placement = isCelebration ? { mode: "center" } : pickPlacement(rect, viewport);
    const pos = computeBubblePos(rect, placement.mode, viewport);

    const showSkip = isAutoWalk && !isCelebration;
    const showNext = isAutoWalk && !isCelebration;
    const stepLabel = walkSequence ? `${walkIndex + 1} of ${walkSequence.length}` : null;

    const bubbleStyle = (() => {
        if (placement.mode === "sheet") {
            return {
                position: "fixed", left: 0, right: 0, bottom: 0,
                maxWidth: "100vw", width: "100vw",
                borderTopLeftRadius: 22, borderTopRightRadius: 22,
                borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
            };
        }
        if (placement.mode === "center" || !pos) {
            return {
                position: "fixed",
                top: "50%", left: "50%",
                transform: "translate(-50%, -50%)",
                width: BUBBLE_WIDTH,
            };
        }
        return {
            position: "fixed",
            top: pos.top, left: pos.left,
            width: BUBBLE_WIDTH,
        };
    })();

    return createPortal(
        <>
            {/* Backdrop click-target — dismisses on tap. Mobile only, so
                the surrounding spotlight tap area feels familiar. */}
            <div
                className="jns-tour-bubble-dismiss-layer"
                onClick={closeBeacon}
                style={{
                    position: "fixed", inset: 0, zIndex: 75,
                    background: "transparent",
                }}
                aria-hidden="true"
                data-testid="tour-bubble-backdrop"
            />
            <div
                role="dialog"
                aria-modal="false"
                aria-labelledby="jns-tour-bubble-heading"
                className={`jns-tour-bubble jns-tour-bubble-${placement.mode}`}
                data-tour-visible={visible ? "true" : "false"}
                data-testid="tour-bubble"
                style={{ ...bubbleStyle, zIndex: 80 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="jns-tour-bubble-inner">
                    <div className="flex items-start gap-3">
                        <div className="shrink-0 jns-tour-avatar-wrap">
                            <MayaAvatar size="xs" sparkle={isCelebration} testid="tour-bubble-avatar" />
                        </div>
                        <div className="flex-1 min-w-0">
                            {stepLabel && (
                                <p className="text-[10px] uppercase tracking-[0.18em] text-rosegold-deep font-bold mb-0.5">
                                    Step {stepLabel}
                                </p>
                            )}
                            <h3
                                id="jns-tour-bubble-heading"
                                className="text-[15px] sm:text-[15.5px] font-bold leading-tight text-ink"
                                data-testid="tour-bubble-heading"
                            >
                                {copy.heading}
                            </h3>
                            <p
                                className="text-[12.5px] sm:text-[13px] text-ink-soft leading-relaxed mt-1.5"
                                data-testid="tour-bubble-body"
                            >
                                {renderBodyWithBadge(copy.body)}
                            </p>
                            {/* iter358 — Optional 30s video walkthrough.
                                Some zones (measurements, AI preview) benefit
                                from a quick visual explainer that text can't
                                deliver. When `copy.video` is set we render a
                                small "Watch" link below the body copy. URL
                                falls back to the JnS YouTube channel if not
                                supplied — admin can plug in real walkthroughs
                                without touching code. */}
                            {copy.video && (copy.video.url || copy.video.label) && (
                                <a
                                    href={copy.video.url || "https://www.youtube.com/@jnscreation"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 mt-2.5 text-[11.5px] font-semibold text-rosegold-deep hover:text-ink"
                                    data-testid="tour-bubble-video-link"
                                >
                                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-rosegold-mist text-rosegold-deep">
                                        <Play size={8} fill="currentColor" />
                                    </span>
                                    {copy.video.label || "Watch 30s walkthrough"}
                                </a>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-4">
                        <div className="flex items-center gap-2 min-w-0">
                            {showSkip ? (
                                <button
                                    type="button"
                                    onClick={skip}
                                    className="text-[11.5px] text-ink-soft hover:text-ink underline-offset-4 hover:underline transition-colors"
                                    data-testid="tour-bubble-skip"
                                >
                                    Skip tour
                                </button>
                            ) : null}
                            {/* iter358 — Language switcher inline with Skip.
                                Tiny segmented control so customers can flip
                                Maya into Hindi or Hinglish in one tap.
                                Choice persists per device via localStorage. */}
                            {Array.isArray(availableLanguages) && availableLanguages.length > 1 && (
                                <div
                                    className="inline-flex items-center gap-0.5 px-0.5 py-0.5 rounded-full bg-rosegold-mist/40 border border-rosegold/25 ml-1"
                                    role="group"
                                    aria-label="Tour language"
                                    data-testid="tour-bubble-lang-switch"
                                >
                                    {availableLanguages.map((l) => (
                                        <button
                                            key={l.code}
                                            type="button"
                                            onClick={() => setLanguage(l.code)}
                                            className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wide transition-colors ${
                                                language === l.code
                                                    ? "bg-wine text-ivory shadow-sm"
                                                    : "text-rosegold-deep hover:text-ink"
                                            }`}
                                            aria-pressed={language === l.code}
                                            data-testid={`tour-lang-${l.code}`}
                                            title={l.label}
                                        >
                                            {l.short}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={showNext ? next : closeBeacon}
                                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-ivory text-[12.5px] font-bold shadow-[0_8px_20px_-10px_rgba(107,44,42,0.6)] hover:-translate-y-0.5 transition-transform"
                                style={{ background: "linear-gradient(135deg, #6B2C2A 0%, #B9663E 60%, #E8A063 100%)" }}
                                data-testid={showNext ? "tour-bubble-next" : "tour-bubble-got-it"}
                            >
                                {isCelebration
                                    ? <>Let&apos;s design <Sparkles size={12} /></>
                                    : showNext
                                        ? <>Next <ArrowRight size={12} /></>
                                        : <>Got it <Sparkles size={11} /></>}
                            </button>
                        </div>
                    </div>
                </div>
                {/* Tail / pointer to the spotlight target. Only rendered
                    in above/below modes — sheet & center don't need it. */}
                {rect && (placement.mode === "below" || placement.mode === "above") && (
                    <span
                        className={`jns-tour-bubble-tail jns-tour-bubble-tail-${placement.mode}`}
                        aria-hidden="true"
                    />
                )}
            </div>
            <style>{`
                .jns-tour-bubble {
                    background: rgba(255, 248, 238, 0.92);
                    backdrop-filter: blur(14px) saturate(1.05);
                    -webkit-backdrop-filter: blur(14px) saturate(1.05);
                    border: 1.5px solid rgba(185, 102, 62, 0.35);
                    border-radius: 18px;
                    box-shadow:
                        0 24px 60px -28px rgba(80, 30, 30, 0.45),
                        0 8px 20px -12px rgba(180, 100, 60, 0.35);
                    opacity: 0;
                    transform-origin: top center;
                    transition: opacity 380ms cubic-bezier(0.16, 1, 0.3, 1),
                                transform 480ms cubic-bezier(0.16, 1, 0.3, 1);
                }
                .jns-tour-bubble[data-tour-visible="true"] { opacity: 1; }
                .jns-tour-bubble-below[data-tour-visible="false"]  { transform: translateY(-12px); }
                .jns-tour-bubble-above[data-tour-visible="false"]  { transform: translateY(12px); }
                .jns-tour-bubble-left[data-tour-visible="false"]   { transform: translateX(12px); }
                .jns-tour-bubble-right[data-tour-visible="false"]  { transform: translateX(-12px); }
                .jns-tour-bubble-sheet[data-tour-visible="false"]  { transform: translateY(24px); }
                .jns-tour-bubble-center[data-tour-visible="false"] { transform: translate(-50%, -46%); }
                .jns-tour-bubble-center[data-tour-visible="true"]  { transform: translate(-50%, -50%); }
                .jns-tour-bubble-inner {
                    padding: 16px 18px 14px 18px;
                }
                .jns-tour-bubble-sheet .jns-tour-bubble-inner {
                    padding: 18px 20px 22px 20px;
                    padding-bottom: max(22px, env(safe-area-inset-bottom));
                }
                .jns-tour-bubble-tail {
                    position: absolute;
                    width: 18px; height: 18px;
                    background: rgba(255, 248, 238, 0.92);
                    border: 1.5px solid rgba(185, 102, 62, 0.35);
                    border-top: none; border-right: none;
                    transform: rotate(45deg);
                    backdrop-filter: blur(14px);
                    -webkit-backdrop-filter: blur(14px);
                    left: 50%;
                    margin-left: -9px;
                }
                .jns-tour-bubble-tail-below {
                    top: -10px;
                    transform: rotate(135deg);
                }
                .jns-tour-bubble-tail-above {
                    bottom: -10px;
                    transform: rotate(-45deg);
                }
                .jns-tour-avatar-wrap {
                    filter: drop-shadow(0 4px 8px rgba(140, 80, 40, 0.35));
                }
                /* iter358.2 — Inline mini-beacon chip rendered inline in
                   the celebration body so customers see what the real
                   amber badge LOOKS like (not just a stray '?' character). */
                .jns-tour-inline-badge {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 18px;
                    height: 18px;
                    border-radius: 9999px;
                    background: linear-gradient(135deg, #FFE9C7 0%, #FFB477 55%, #E8A063 100%);
                    color: #4A1A1A;
                    border: 1.5px solid rgba(180, 90, 40, 0.85);
                    box-shadow:
                        0 3px 6px -2px rgba(180, 80, 40, 0.45),
                        0 0 0 2px rgba(255, 248, 238, 0.95);
                    font-family: ui-serif, Georgia, "DM Serif Display", serif;
                    font-weight: 800;
                    font-size: 11px;
                    line-height: 1;
                    margin: 0 3px;
                    vertical-align: middle;
                    transform: translateY(-1px);
                }
                @media (prefers-reduced-motion: reduce) {
                    .jns-tour-bubble { transition: opacity 200ms ease !important; transform: none !important; }
                }
            `}</style>
        </>,
        document.body,
    );
}
